import React from "react";
import { HashRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import ThemeProvider from '@material-ui/styles/ThemeProvider';
import { createMuiTheme } from '@material-ui/core/styles';
import OronaLineContext from './AppContext';
import * as model from './model';
import * as langUtils from './core/languageUtils';
import TagManager from 'react-gtm-module';
import ReactGA from 'react-ga4';
import firebase from 'firebase/app';

import 'firebase/auth'
import "firebase/messaging";

import { isIOS, isChrome, isMobile } from 'mobile-device-detect';
import * as api from './api';
import Cookies from 'universal-cookie';


interface AppProps { }

interface AppState {
  loggedIn: boolean,
  user: model.User,
  currentLang: model.Lang
  token: string
}

const theme = createMuiTheme({
  typography: {
    fontFamily: "'Montserrat'",
  },
  palette: {
      primary: {
          light: '#ade754',
          main: '#00a532',
          dark: '#478500'
      },
      secondary: {
        main: 'rgb(255, 255, 255) transparent'
      }
  },
});

// Wrap each imported route in lazy import 
const Login = React.lazy(() => import("./components/login/Login"));
const OronaMove2 = React.lazy(() => import("./components/oronamove/OronaMove2"));
const OronaNews = React.lazy(() => import("./components/news/OronaNews"));
const OronaNewsDetail = React.lazy(() => import("./components/news/OronaNewsDetail"));
const MyOrona = React.lazy(() => import("./components/myorona/MyOrona"));
const User = React.lazy(() => import("./components/user/User"));
const ChangePassword = React.lazy(() => import("./components/user/ChangePassword"));
const LegalInfo = React.lazy(() => import("./components/info/LegalInfo"));
const CookiesInfo = React.lazy(() => import("./components/info/CookiesInfo"));
const UserAgreement = React.lazy(() => import("./components/info/UserAgreement"));
const DocumentDetail = React.lazy(() => import("./components/utils/DocumentDetail"));
const Logout = React.lazy(() => import("./components/login/Logout"));
const NotFound = React.lazy(() => import("./components/utils/NotFound"));

class App extends React.Component<AppProps, AppState> {
  constructor(props: AppProps, state: AppState) {
    super(props, state);
    this.state = {
      loggedIn: false,
      user: {
        firstName: "",
        lastName: '',
        emailAddress: "",
        userId: ""
      },
      currentLang: langUtils.getLanguage(langUtils.getDefaultLanguage().key),
      token: ""
    };

    let gtmKey = process.env.REACT_APP_GTM_KEY;
    if (gtmKey && gtmKey !== undefined) {
        const tagManagerArgs = {
            gtmId: gtmKey
        }
        TagManager.initialize(tagManagerArgs);
    }

    let gaKey = process.env.REACT_APP_GA_KEY;
    if (gaKey && gaKey !== undefined) {
        // ReactGA.initialize(gaKey, { debug: true }); 
        ReactGA.initialize(gaKey);
    }


	if (isChrome && !isIOS) {
	    const firebaseConfig = {
	        apiKey: process.env.REACT_APP_FCM_APIKEY,
	        authDomain: process.env.REACT_APP_FCM_AUTHDOMAIN,
	        databaseURL: process.env.REACT_APP_FCM_DATABASEURL,
	        projectId: process.env.REACT_APP_FCM_PROJECTID,
	        storageBucket: process.env.REACT_APP_FCM_STORAGEBUCKET,
	        messagingSenderId: process.env.REACT_APP_FCM_MESSAGINSENDERID,
	        appId: process.env.REACT_APP_FCM_APPID,
	        measurementId: process.env.REACT_APP_GA_KEY
	    };

	    // Initialize Firebase
	    firebase.initializeApp(firebaseConfig);
	}
  } 

	componentWillMount() {
		const data: string | null = localStorage.getItem("data");
		if (data) {
	      const state: AppState = JSON.parse(atob(data));
	      langUtils.changeLanguage(langUtils.getLanguage(state.user.languageId).app);
	      this.setState(state);
	    }

		this.updateStatusNotificationOnChange();
	}

  onUserLoginSuccess = (data: model.GetUserResponse) => {
    // https://www.robinwieruch.de/local-storage-react
    this.setState({ 
      loggedIn: true,
      user: data.user,
      token: data.token,
      currentLang: langUtils.getLanguage(data.user.languageId ? data.user.languageId : ""),
    });
	langUtils.changeLanguage(langUtils.getLanguage(data.user.languageId).app);
    localStorage.setItem("data", btoa(JSON.stringify(this.state)));
  }
  
  updateLanguage = (lang: model.Lang) => {
    this.setState({
      currentLang: lang
    });
    langUtils.changeLanguage(lang.app);
    let data: AppState = this.state;
    data.user.languageId = lang.content;
    this.setState(data);
    localStorage.setItem("data", btoa(JSON.stringify(data)));
  }
  
  updateData = (data: model.GetUserResponse) => {
    this.setState({
      loggedIn: true,
      user: data.user,
      token: data.token
    });
    localStorage.setItem("data", btoa(JSON.stringify(this.state)));
  }

	updateStatusNotificationOnChange(){
		if (isChrome && !isIOS) {
		    
			navigator.permissions.query({name:'notifications'}).then(function(notificationPerm) {
	
		        notificationPerm.onchange = function() {
				    const data: string | null = localStorage.getItem("data");
					    if (data) {
					      	const state: AppState = JSON.parse(atob(data));
							const cookies = new Cookies();
							if(notificationPerm.state == 'granted' && cookies.get('isTokenSendedToServer') != 'true'){
								cookies.set('isTokenSendedToServer', 'true');
				                firebase.messaging().getToken().then((refreshedToken: string) => {
									api.updateUserFcmtokens(state.user, refreshedToken, state.token);
									setTimeout(function(){}, 50000);
									cookies.remove('isTokenSendedToServer');
			                    }).catch((err) => {
									cookies.remove('isTokenSendedToServer');
			                    });
							}else if (notificationPerm.state == 'denied' && cookies.get('isTokenDeletedFromServer') != 'true'){
								cookies.set('isTokenDeletedFromServer', 'true');
								firebase.messaging().deleteToken().then(() => {
									api.cleanUserFcmtokens(state.user, state.token);
									setTimeout(function(){}, 50000);
									cookies.remove('isTokenDeletedFromServer');
					            }).catch((err) => {
									api.cleanUserFcmtokens(state.user, state.token);
									setTimeout(function(){}, 50000);
									cookies.remove('isTokenDeletedFromServer');
					            });
							}
					    }
		        };
	    	});
        }
	}
	
  render() {
      return (
        <MuiThemeProvider>
        <ThemeProvider theme={theme}>
          <OronaLineContext.Provider
            value={{
              user: this.state.user,
              language: this.state.currentLang.content,
              loggedIn: this.state.loggedIn,
              token: this.state.token,
              update: this.updateData,
              updateLanguage: this.updateLanguage              
            }}>
            <OronaLineContext.Consumer>
              {({ user, loggedIn, token, language, update, updateLanguage }) => (
                <Router>
                  <React.Suspense fallback={<div>...</div>}>
                  <Switch>
                    <Route
                      path="/"
                      exact
                      render={(props) => loggedIn ?
                        <Redirect {...props} to="/oronamove" />
                        : <Redirect {...props} to="/login" />}
                    />
  
                    <Route 
                      path={"/login"} 
                      exact 
                      render={(props) => <Login {...props} onLogin={this.onUserLoginSuccess} />} 
                    />

                    <Route
                      path={"/oronamove"}
                      exact
                      render={(props) => loggedIn ? 
                        <OronaMove2 language={language} updateLanguage={updateLanguage} {...props} user={user} token={token} /> 
                        : <Redirect {...props} to="/login" />} 
                      />                    
  
                    <Route
                      path={"/news"}
                      exact
                      render={(props) => loggedIn ? 
                        <OronaNews {...props} language={language} updateLanguage={updateLanguage} user={user} token={token} /> 
                        : <Redirect {...props} to="/login" />} 
                      />
  
                    <Route
                      path={"/news/:id"}
                      exact
                      render={(props) => loggedIn ? 
                        <OronaNewsDetail language={language} updateLanguage={updateLanguage} {...props} user={user} token={token} /> 
                        : <Redirect {...props} to="/login" />}
                    />
  
                    <Route 
                      path={"/myorona"}
                      render={(props) => loggedIn ?
                        <MyOrona {...props} language={language} updateLanguage={updateLanguage} user={user} token={token}/>
                        : <Redirect {...props} to="/login" />}
                    />

                     <Route
                      path={"/user"}
                      exact
                      render={(props) => loggedIn ?
                         <User language={language} updateLanguage={updateLanguage} {...props} user={user} token={token}/> 
                         : <Redirect {...props} to="/login" />} 
                    />
  
                    <Route
                      path={"/changepasswd/:userId"}
                      render={(props) => loggedIn ? 
                        <ChangePassword {...props} language={language} updateLanguage={updateLanguage} user={user} token={token} updateUser={update}/> 
                        : <Redirect {...props} to="/login" />} 
                    />            
  
                    <Route
                      path={"/legalinfo"}
                      render={(props) => loggedIn ? 
                        <LegalInfo {...props} language={language} updateLanguage={updateLanguage} user={user} token={token} /> 
                        : <Redirect {...props} to="/login" />} 
                    />
                    
                    <Route 
                      path={"/cookies"} 
                      render={(props) => loggedIn ?
                        <CookiesInfo {...props} language={language} updateLanguage={updateLanguage} user={user} token={token} />
                        : <Redirect {...props} to="/login" />}
                    />

                    <Route
                      path={"/agreement"}
                      exact
                      render={(props) => loggedIn ? 
                        <UserAgreement {...props} language={language} user={user} token={token}/> 
                        : <Redirect {...props} to="/login" />} 
                    /> 

                    <Route 
                      path={`/document/:section/:articleId/:index?`} 
                      exact 
                      render={(props) => loggedIn ?
                        <DocumentDetail language={language} user={user} token={token}/> 
                        : <Redirect {...props} to="/login" />}
                    />

                    <Route 
                      path={`/logout`}                       
                      render={(props) => loggedIn ? 
                        <Logout {...props} />
                        : <Redirect {...props} to="/login" />}
                    />

                    <Route 
                      render={(props) => loggedIn ?
                        <NotFound {...props} />
                        : <Redirect {...props} to="/login" />} 
                    />
                  </Switch>
                  </React.Suspense>
                </Router>
              )}
            </OronaLineContext.Consumer>
          </OronaLineContext.Provider>
        </ThemeProvider>
        </MuiThemeProvider>
      );
  }
}

export default App;
